// src/PayRoll/PayRoll_Report/PayrollReportPrint.jsx
import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const BASE_URL = "http://nissi-india.com";

const monthNames = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

const monthNoToName = (m) => {
  const n = Number(m);
  if (!Number.isFinite(n) || n < 1 || n > 12) return "";
  return monthNames[n - 1];
};

const n2 = (v) => {
  const num = Number(v ?? 0);
  return Number.isFinite(num) ? num.toFixed(2) : "0.00";
};

const sum = (rows, key) => rows.reduce((a, r) => a + (Number(r?.[key] ?? 0) || 0), 0);

export default function PayrollReportPrint() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [fatal, setFatal] = useState("");

  const [meta, setMeta] = useState({
    companyName: "Nissi Infotech",
    companyLine1: "Nissi",
    companyLine2: "CIVIL CONSTRUCTION",
    addressLine: "3/42-12, Nagappa colony, kavundampalayam-641030 . Tamil Nadu. India .",
    phone: "Phone No : 9003744644",
    email: "infraconsoft-implementation@nissiinfotech.in",
    // ✅ put a real logo URL from API if you have, else you can hardcode
    logoUrl: "",
  });

  const params = useMemo(() => {
    const qs = new URLSearchParams(window.location.search);

    const finYearId = (qs.get("finYearId") || "").trim();
    const branch = (qs.get("branch") || qs.get("branchId") || "").trim();
    const year = (qs.get("year") || "").trim();

    const monthNoRaw = (qs.get("monthNo") || "").trim();
    const monthNo = monthNoRaw && !isNaN(Number(monthNoRaw)) ? Number(monthNoRaw) : null;

    const monthText =
      (qs.get("monthName") || "").trim() ||
      (qs.get("month") || "").trim() ||
      (monthNo ? monthNoToName(monthNo) : "");

    const department = (qs.get("departmentId") || qs.get("department") || "").trim();
    const employeecode = (qs.get("empCode") || qs.get("employeecode") || "").trim();
    const employeename = (qs.get("empName") || qs.get("employeename") || "").trim();

    const isPF = (qs.get("pfnonpf") || qs.get("isPF") || "").trim(); // Yes/No/Honorarium
    const paymode = (qs.get("paymode") || "").trim();

    const branchName =
      (qs.get("branchName") || qs.get("branchText") || qs.get("branchLabel") || "").trim();

    return {
      finYearId,
      branch,
      branchName,
      monthNo,
      monthText,
      year,
      department,
      employeecode,
      employeename,
      isPF,
      paymode,
    };
  }, []);

  useEffect(() => {
    if (!params.branch || !params.year || !params.monthNo) {
      setFatal("Select Mandatory Fields (Branch, Month, Year)");
      setLoading(false);
    }
  }, [params.branch, params.monthNo, params.year]);

  // Header like Crystal
  const headerTitle = useMemo(() => {
    const pfLabel = (params.isPF || "").toUpperCase(); // PF/NON PF shown in crystal
    const monthLabel = (params.monthText || "").toUpperCase();
    const yearLabel = (params.year || "").toUpperCase();
    const brLabel = (params.branchName || "").toUpperCase();

    // Crystal looks like: BANK SALARY LIST - PF - FOR THE MONTH OF FEBRUARY2026 - HEAD-OFFICE
    return `BANK SALARY LIST - ${pfLabel || ""} - FOR THE MONTH OF ${monthLabel} ${yearLabel} - ${brLabel || ""}`
      .replace(/\s+/g, " ")
      .trim();
  }, [params]);

  useEffect(() => {
    const fetchPrint = async () => {
      if (!params.branch || !params.year || !params.monthNo) return;

      try {
        setLoading(true);
        setFatal("");

        const res = await axios.get(`${BASE_URL}/api/payroll-report/print-data`, {
          params: {
            finYearId: params.finYearId,
            branch: params.branch,
            monthNo: params.monthNo,
            monthName: params.monthText,
            year: params.year,
            department: params.department,
            employeename: params.employeename,
            employeecode: params.employeecode,
            isPF: params.isPF,
            paymode: params.paymode,
          },
        });

        if (res?.data?.success) {
          setRows(res.data.rows || []);
          if (res.data.company) setMeta((m) => ({ ...m, ...res.data.company }));
        } else {
          setFatal(res?.data?.message || "Failed to load print data");
        }
      } catch (e) {
        setFatal(e?.response?.data?.message || e?.message || "Print data error");
      } finally {
        setLoading(false);
        setTimeout(() => {
          if (!fatal) window.print();
        }, 400);
      }
    };

    fetchPrint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.branch, params.monthNo, params.year]);

  // Totals (Crystal-like)
  const totals = useMemo(() => {
    const gross = sum(rows, "fld_grosssalary");
    const pfAmt = sum(rows, "pfamt");
    const adv = sum(rows, "fld_loandetection");
    const advBal = sum(rows, "loandetection");
    const others = sum(rows, "fld_advance");
    const esi = sum(rows, "fld_esi");
    const tds = sum(rows, "fld_tds");
    const net = sum(rows, "fld_Netpay");
    return { gross, pfAmt, adv, advBal, others, esi, tds, net };
  }, [rows]);

  const signatureText = (paymode) => {
    const v = String(paymode || "").toLowerCase();
    if (!v) return "";
    if (v.includes("bank")) return "Through Bank";
    if (v.includes("cash")) return "Cash";
    return paymode;
  };

  return (
    <div style={{ padding: 18, fontFamily: "Times New Roman, serif", color: "#000" }}>
      <style>{`
        @page { margin: 10mm; }
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        .box { border: 1px solid #000; }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #000; padding: 4px 6px; font-size: 12px; vertical-align: middle; }
        th { font-weight: 700; text-align: center; }
        td.num { text-align: right; }
        td.center { text-align: center; }
      `}</style>

      {/* Buttons */}
      <div className="no-print" style={{ display: "flex", gap: 14, marginBottom: 10 }}>
        <button onClick={() => window.print()} style={{ cursor: "pointer" }}>Print</button>
        <button onClick={() => window.close()} style={{ cursor: "pointer" }}>Close</button>
      </div>

      {/* Header Box like Crystal */}
      <div className="box" style={{ padding: 10, marginBottom: 10 }}>
        <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 140px", alignItems: "center" }}>
          {/* Logo (left) */}
          <div style={{ display: "flex", justifyContent: "center" }}>
            {meta.logoUrl ? (
              <img
                src={meta.logoUrl}
                alt="logo"
                style={{ width: 120, height: 70, objectFit: "contain" }}
              />
            ) : (
              <div style={{ width: 120, height: 70, border: "1px solid #000" }} />
            )}
          </div>

          {/* Center company text */}
          <div style={{ textAlign: "center", lineHeight: 1.25 }}>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{meta.companyName}</div>
            <div style={{ fontSize: 13, fontWeight: 700 }}>{meta.companyLine1}</div>
            <div style={{ fontSize: 13, fontWeight: 700 }}>{meta.companyLine2}</div>
            <div style={{ fontSize: 12 }}>{meta.addressLine}</div>
            <div style={{ fontSize: 12 }}>{meta.phone}</div>
            <div style={{ fontSize: 12 }}>{meta.email}</div>
          </div>

          {/* Right spacer (Crystal has blank) */}
          <div />
        </div>
      </div>

      {/* Title */}
      <div style={{ textAlign: "center", fontWeight: 700, margin: "6px 0 10px" }}>
        {headerTitle}
      </div>

      {fatal ? (
        <div className="box" style={{ padding: 14, fontWeight: 700 }}>
          {fatal}
        </div>
      ) : loading ? (
        <div>Loading...</div>
      ) : (
        <>
          {/* Main table with Crystal-like grouped header */}
          <table className="box">
            <thead>
              <tr>
                <th rowSpan={2} style={{ width: 50 }}>S.No</th>
                <th rowSpan={2} style={{ width: 70 }}>Code</th>
                <th rowSpan={2} style={{ width: 160 }}>EMP Name</th>
                <th rowSpan={2} style={{ width: 140 }}>Designation</th>

                <th rowSpan={2} style={{ width: 90 }}>Gross salary</th>
                <th rowSpan={2} style={{ width: 90 }}>Basic+DA</th>
                <th rowSpan={2} style={{ width: 55 }}>LOP</th>
                <th rowSpan={2} style={{ width: 95 }}>Paid Basic+DA</th>
                <th rowSpan={2} style={{ width: 90 }}>Conveyance</th>
                <th rowSpan={2} style={{ width: 95 }}>Educational</th>
                <th rowSpan={2} style={{ width: 90 }}>HRA</th>

                <th colSpan={6}>Deductions</th>

                <th rowSpan={2} style={{ width: 95 }}>Net Pay</th>
                <th rowSpan={2} style={{ width: 120 }}>Signature</th>
              </tr>

              <tr>
                <th style={{ width: 70 }}>PF</th>
                <th style={{ width: 80 }}>Advance</th>
                <th style={{ width: 90 }}>Advance Bal.</th>
                <th style={{ width: 80 }}>Others</th>
                <th style={{ width: 70 }}>ESI</th>
                <th style={{ width: 70 }}>TDS</th>
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={19} className="center" style={{ padding: 14 }}>
                    No Records Found
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={idx}>
                    <td className="center">{idx + 1}</td>
                    <td className="center">{r.fld_employeecode}</td>
                    <td className="center">{r.fld_employee}</td>
                    <td className="center">{r.fld_designationname}</td>

                    <td className="num">{n2(r.fld_grosssalary)}</td>
                    <td className="num">{n2(r.fld_basicpay)}</td>
                    <td className="num">{n2(r.fld_lopdays)}</td>
                    <td className="num">{n2(r.fld_paidbasic)}</td>
                    <td className="num">{n2(r.fld_conveyance)}</td>
                    <td className="num">{n2(r.fld_educational)}</td>
                    <td className="num">{n2(r.fld_hra)}</td>

                    <td className="num">{n2(r.pfamt)}</td>
                    <td className="num">{n2(r.fld_loandetection)}</td>
                    <td className="num">{n2(r.loandetection)}</td>
                    <td className="num">{n2(r.fld_advance)}</td>
                    <td className="num">{n2(r.fld_esi)}</td>
                    <td className="num">{n2(r.fld_tds)}</td>

                    <td className="num">{n2(r.fld_Netpay)}</td>
                    <td className="center">{signatureText(r.paymode || params.paymode)}</td>
                  </tr>
                ))
              )}

              {rows.length > 0 && (
                <tr>
                  <td colSpan={4} className="center" style={{ fontWeight: 700 }}>Total</td>

                  <td className="num" style={{ fontWeight: 700 }}>{n2(totals.gross)}</td>
                  <td /> {/* basic total not shown in crystal */}
                  <td /> {/* lop */}
                  <td className="num" style={{ fontWeight: 700 }}>
                    {/* Paid basic total not in your totals; crystal may show. keep blank or sum if you want */}
                  </td>
                  <td />
                  <td />
                  <td />

                  <td className="num" style={{ fontWeight: 700 }}>{n2(totals.pfAmt)}</td>
                  <td className="num" style={{ fontWeight: 700 }}>{n2(totals.adv)}</td>
                  <td className="num" style={{ fontWeight: 700 }}>{n2(totals.advBal)}</td>
                  <td className="num" style={{ fontWeight: 700 }}>{n2(totals.others)}</td>
                  <td className="num" style={{ fontWeight: 700 }}>{n2(totals.esi)}</td>
                  <td className="num" style={{ fontWeight: 700 }}>{n2(totals.tds)}</td>

                  <td className="num" style={{ fontWeight: 700 }}>{n2(totals.net)}</td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>

          {/* Summary box (bottom-left) like Crystal */}
          {rows.length > 0 && (
            <div style={{ display: "flex", marginTop: 10 }}>
              <table className="box" style={{ width: 280 }}>
                <tbody>
                  <tr>
                    <td style={{ fontWeight: 700 }}>GROSS SALARY</td>
                    <td className="num" style={{ fontWeight: 700 }}>{n2(totals.gross)}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 700 }}>PF</td>
                    <td className="num" style={{ fontWeight: 700 }}>{n2(totals.pfAmt)}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 700 }}>ADVANCE</td>
                    <td className="num" style={{ fontWeight: 700 }}>{n2(totals.adv)}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 700 }}>OTHERS</td>
                    <td className="num" style={{ fontWeight: 700 }}>{n2(totals.others)}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 700 }}>ESI</td>
                    <td className="num" style={{ fontWeight: 700 }}>{n2(totals.esi)}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 700 }}>TDS</td>
                    <td className="num" style={{ fontWeight: 700 }}>{n2(totals.tds)}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 700 }}>NET SALARY</td>
                    <td className="num" style={{ fontWeight: 700 }}>{n2(totals.net)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Footer labels */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 28,
              fontSize: 13,
            }}
          >
            <div>Prepared By</div>
            <div>Checked By</div>
            <div>Sr.Manager Accounts</div>
          </div>
        </>
      )}
    </div>
  );
}
